import React, { useState, useEffect } from 'react';
import SideMenu from "../../component/SideMenu";
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseurl } from '../../baseurl';
import { Eye, User, MapPin, FileText, ChevronLeft, ChevronRight, Upload, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const AllSubmission = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // --- States for Filtering ---
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reportTypeFilter, setReportTypeFilter] = useState('all');
  
  // --- States for Pagination ---
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const adminToken = localStorage.getItem('adminToken');

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseurl}getAllSubmissionsAdmin`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      if (response.data.success) {
        setSubmissions(response.data.data);
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch admin submissions' });
    } finally {
      setTimeout(() => setLoading(false), 600);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [adminToken]);

  // --- Logic: Delete Submission ---
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${baseurl}admin/submissions/${id}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });

        if (response.data.success) {
          Swal.fire('Deleted!', 'Submission has been deleted.', 'success');
          // Remove deleted record locally to avoid re-fetching
          setSubmissions(prev => prev.filter(item => item.id !== id));
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: err.response?.data?.message || 'Failed to delete submission.'
        });
      }
    }
  };

  // --- Logic: Dynamic Dropdown Options ---
  const distinctReportTypes = [...new Set(submissions.map(s => s.project_data?.reportType).filter(Boolean))];

  // --- Logic: Filtering ---
  const filteredData = submissions.filter(proj => {
    const matchesSearch = `${proj.full_name} ${proj.property_id} ${proj.project_data?.address}`
      .toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || proj.status.toLowerCase() === statusFilter.toLowerCase();
    
    const matchesReport = reportTypeFilter === 'all' || proj.project_data?.reportType === reportTypeFilter;

    return matchesSearch && matchesStatus && matchesReport;
  });

  // --- Logic: Pagination Calculations ---
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', { 
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase();
    if (s === 'draft') return <span className="badge-status draft">Draft</span>;
    if (s === 'pending' || s === 'queued') return <span className="badge-status pending">Pending</span>;
    if (s === 'completed') return <span className="badge-status completed">Completed</span>;
    return <span className="badge-status">{status}</span>;
  };

  const SkeletonRow = () => (
    <tr className="skeleton-row">
      <td colSpan="8"><div className="skeleton-line"></div></td>
    </tr>
  );

  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', display: 'flex' }}>
        {/* <SideMenu /> */}
        <div className="profile-content-container p-4" style={{ flex: 1, overflow: 'auto' }}>
          
          <div className="mb-4">
            <h2 className="fw-bold mb-1" style={{ color: '#1B2559' }}>Admin: All Submissions</h2>
            <p className="text-muted m-0">Filter and manage all user property projects.</p>
          </div>

          {/* CONTROL BOX (Filters) */}
          <div className="card border-0 shadow-sm mb-4 p-3" style={{ borderRadius: '15px' }}>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="small fw-bold text-muted mb-1">SEARCH</label>
                <div className="input-group border rounded-3 bg-light m-0">
                  <span className="input-group-text bg-transparent border-0"><i className="fa-solid fa-magnifying-glass text-muted"></i></span>
                  <input 
                    type="text" 
                    className="form-control bg-transparent border-0 shadow-none" 
                    placeholder="ID, Address, or Name..." 
                    onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
                  />
                </div>
              </div>

              <div className="col-md-2">
                <label className="small fw-bold text-muted mb-1">STATUS</label>
                <select className="form-select border-0 bg-light rounded-3 shadow-none" value={statusFilter} onChange={(e) => {setStatusFilter(e.target.value); setCurrentPage(1);}}>
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div className="col-md-3">
                <label className="small fw-bold text-muted mb-1">REPORT TYPE</label>
                <select className="form-select border-0 bg-light rounded-3 shadow-none" value={reportTypeFilter} onChange={(e) => {setReportTypeFilter(e.target.value); setCurrentPage(1);}}>
                  <option value="all">All Report Types</option>
                  {distinctReportTypes.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>

              <div className="col-md-3">
                <label className="small fw-bold text-muted mb-1">PER PAGE</label>
                <select className="form-select border-0 bg-light rounded-3 shadow-none" value={rowsPerPage} onChange={(e) => {setRowsPerPage(Number(e.target.value)); setCurrentPage(1);}}>
                  <option value={10}>10 rows</option>
                  <option value={25}>25 rows</option>
                  <option value={50}>50 rows</option>
                </select>
              </div>
            </div>
          </div>

          {/* TABLE */}
          <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4 border-0 text-muted small fw-bold">Sr No. </th>
                    <th className="ps-4 border-0 text-muted small fw-bold">PROPERTY ID</th>
                    <th className="border-0 text-muted small fw-bold">CLIENT</th>
                    <th className="border-0 text-muted small fw-bold">DETAILS</th>
                    <th className="border-0 text-muted small fw-bold text-center">STATUS</th>
                    <th className="border-0 text-muted small fw-bold text-center">CREDITS</th>
                    <th className="border-0 text-muted small fw-bold">DATE</th>
                    <th className="border-0 text-muted small fw-bold text-center">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [1,2,3,4,5].map(i => <SkeletonRow key={i} />)
                  ) : currentRows.length > 0 ? (
                    currentRows.map((proj, index) => (
                      <tr key={proj.id}>
                        <td><span className="text-muted small fw-bold">{indexOfFirstRow + index + 1}</span></td>
                        <td className="ps-4">
                           <div className="fw-bold text-primary small">{proj.property_id}</div>
                           <div className="text-muted" style={{fontSize: '11px'}}>{proj.project_data?.reportType}</div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="avatar-user me-2"><User size={14} /></div>
                            <div>
                              <div className="fw-bold text-dark" style={{fontSize: '13px'}}>{proj.full_name}</div>
                              <div className="text-muted small">{proj.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column" style={{maxWidth: '200px'}}>
                            <span className="small text-dark text-truncate fw-medium"><MapPin size={12} className="me-1" />{proj.project_data?.address}</span>
                            <span className="small text-muted mt-1 text-truncate"><FileText size={12} className="me-1" />{proj.project_data?.lotArea}</span>
                          </div>
                        </td>
                        <td className="text-center">{getStatusBadge(proj.status)}</td>
                        <td className="text-center fw-bold text-dark">{proj.project_data?.totalCredits || '0'} CR</td>
                        <td className="text-muted small">{formatDate(proj.created_at)}</td>
                        <td className="text-center">
                          <a href={`https://map.motherwit.in/report/${proj.property_id}`} target='_blank' rel="noreferrer" className="btn btn-light btn-sm rounded-circle me-1" title="View"><Eye size={15} /></a>
                          <Link to={`/upload-data/${proj.id}`} className="btn btn-light btn-sm rounded-circle me-1" title="Upload"><Upload size={15} /></Link>
                          <button onClick={() => handleDelete(proj.id)} className="btn btn-light text-danger btn-sm rounded-circle me-1" title="Delete"><Trash2 size={15} /></button>
                          <Link to={`/submission-details/${proj.id}`} className="btn btn-primary btn-sm rounded-pill px-3">Review</Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="8" className="text-center py-5 text-muted">No submissions match your filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION FOOTER */}
            {!loading && totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top bg-light">
                <div className="small text-muted">
                  Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredData.length)} of {filteredData.length} entries
                </div>
                <div className="d-flex gap-2">
                  <button 
                    className="btn btn-sm btn-outline-secondary rounded-3" 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button 
                      key={i} 
                      className={`btn btn-sm rounded-3 ${currentPage === i + 1 ? 'btn-primary' : 'btn-outline-light text-dark'}`}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button 
                    className="btn btn-sm btn-outline-secondary rounded-3" 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <style>{`
        .avatar-user { width: 32px; height: 32px; background: #E9EDF7; color: #4318FF; display: flex; align-items: center; justify-content: center; border-radius: 50%; }
        .badge-status { padding: 4px 10px; border-radius: 6px; font-size: 10px; font-weight: 700; text-transform: uppercase; }
        .badge-status.draft { background: #E1EFFF; color: #0061FF; }
        .badge-status.pending { background: #FFF4E5; color: #FF8C00; }
        .badge-status.completed { background: #E2F9EB; color: #23BD5C; }
        .skeleton-row td { padding: 25px 0; }
        .skeleton-line { height: 20px; width: 90%; margin: 0 auto; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: skeleton-loading 1.5s infinite; border-radius: 4px; }
        @keyframes skeleton-loading { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>
    </div>
  );
};

export default AllSubmission;